// Marketing is now served by the edge itself, so all /api calls are
// same-origin. VITE_ORI_API_URL still overrides for the rare case of
// running `vite dev` against a remote edge (e.g. pointing the local
// landing at the dev deploy).
function apiBase(): string {
  return import.meta.env.VITE_ORI_API_URL || '';
}

export type StartResponse = {
  status: 'verification_required';
  onboarding_session_id: string;
  verification: { to_number: string; code: string; expires_at: string };
  instructions?: string;
  flow?: string;
  plan_id?: string | null;
};

export class OriApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
  constructor(opts: { status: number; code?: string; body?: unknown; message?: string }) {
    super(opts.message || opts.code || `HTTP ${opts.status}`);
    this.name = 'OriApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.body = opts.body;
  }
}

async function readJsonSafe(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export type OnboardingStatusResponse = {
  status: string;
  ori_number?: string | null;
  self_invite?: boolean;
};

// Poll the verify session so the web tab can flip to "you're in" the moment
// the SMS lands (the backend keeps completed sessions readable for a
// retention window precisely for this).
export async function getOnboardingStatus(sessionId: string): Promise<OnboardingStatusResponse> {
  const res = await fetch(`${apiBase()}/api/onboarding/${encodeURIComponent(sessionId)}/status`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const json = await readJsonSafe(res);
  if (!res.ok) {
    throw new OriApiError({ status: res.status, code: json.error as string | undefined, body: json });
  }
  return json as OnboardingStatusResponse;
}

// Door-B web login: once the verify session is `completed`, trade the
// onboarding_session_id for a browser session cookie so the site recognizes
// the viewer (Nav flips to the signed-in pill, /me opens the dashboard).
// Best-effort — failure just leaves the tab anonymous.
export async function exchangeOnboardingForSession(sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase()}/api/web-session/from-onboarding`, {
      method: 'POST',
      // CRITICAL: include credentials so the Set-Cookie (ori_session) lands.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function startOnboarding(opts: {
  /** What to call the user. Required by the backend (name_required). */
  name?: string;
  /** Required age gate (replaced the date-of-birth wheel). */
  ageConfirmed?: boolean;
  /** Optional — only sent if the date-wheel path is re-enabled. */
  birthday?: string;
  /** Acceptance moment; shared with the verify message so both match. */
  acceptedAt?: Date;
  /** Per-sheet idempotency nonce so retries reuse the same code/number. */
  clientToken: string;
  /** Promotion-channel slug captured from a /c/<channel> landing URL. */
  channel?: string;
  /** Painpoint slug captured from a /lp/<painpoint> landing URL. */
  painpoint?: string;
  /** Propagation platform (utm_source) for painpoint traffic. */
  platform?: string;
  /** Ori journey the campaign promotes (plan | invite). */
  solution?: string;
  /** Raw whitelisted UTM params for downstream/external analytics parity. */
  utm?: Record<string, string>;
  /** Anonymous PostHog browser id, for pre-onboarding clickstream stitching. */
  distinctId?: string;
}): Promise<StartResponse> {
  const body: Record<string, unknown> = {
    tos_accepted_at: (opts.acceptedAt ?? new Date()).toISOString(),
    client_token: opts.clientToken,
  };
  if (opts.name) body.name = opts.name;
  if (opts.ageConfirmed) body.age_confirmed = true;
  if (opts.birthday) body.birthday = opts.birthday;
  if (opts.channel) body.ref = opts.channel;
  if (opts.painpoint) body.painpoint = opts.painpoint;
  if (opts.platform) body.platform = opts.platform;
  if (opts.solution) body.solution = opts.solution;
  if (opts.utm && Object.keys(opts.utm).length > 0) body.utm = opts.utm;
  if (opts.distinctId) body.distinct_id = opts.distinctId;
  const res = await fetch(`${apiBase()}/api/onboarding/start`, {
    method: 'POST',
    // CRITICAL: must include cookies so the Set-Cookie from the returning branch
    // (ori_session) actually lands in the browser. credentials: 'omit' will drop it.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await readJsonSafe(res);
  if (!res.ok) {
    throw new OriApiError({ status: res.status, code: json.error as string | undefined, body: json });
  }
  return json as StartResponse;
}
